import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Search,
    Plus,
    User,
    Home,
    DollarSign,
    Mail,
    Clock,
    ChevronRight,
    Edit,
    Trash2,
    AlertTriangle,
    RefreshCw,
    Filter,
    Grid,
    List,
    Building2
} from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useLandlordStore, Unit } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

type UnitStatus = "Available" | "Occupied" | "Maintenance" | "pending_approval";
type PaymentStatus = "Paid" | "Due" | "Overdue";
type ViewMode = 'grid' | 'list';

interface EditUnitModalProps {
    unit: Unit;
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedData: Partial<Unit>) => Promise<void>;
}

const EditUnitModal: React.FC<EditUnitModalProps> = ({ unit, isOpen, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        unit_number: unit.unit_number,
        rent_amount: unit.rent_amount,
        status: unit.status,
        bedrooms: unit.bedrooms || 1,
        bathrooms: unit.bathrooms || 1,
        unit_type: unit.unit_type || 'Residential',
        description: unit.description || '',
        amenities: unit.amenities || [] as string[]
    });
    const [isSaving, setIsSaving] = useState(false);

    const allAmenities = [
        "WiFi", "Parking", "Backup Generator", "Borehole",
        "Swimming Pool", "Gym", "24/7 Security", "CCTV",
        "Balcony", "Furnished", "Elevator"
    ];

    const handleToggleAmenity = (amenity: string) => {
        setFormData(prev => ({
            ...prev,
            amenities: prev.amenities.includes(amenity)
                ? prev.amenities.filter(a => a !== amenity)
                : [...prev.amenities, amenity]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onSave(formData);
            onClose();
        } catch (error) {
            console.error('Failed to update unit:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-light-text dark:text-dark-text">
                        Edit Unit {unit.unit_number}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-full transition-colors"
                    >
                        <ArrowLeft size={20} className="text-light-subtle dark:text-dark-subtle" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                                Unit Number
                            </label>
                            <input
                                type="text"
                                value={formData.unit_number}
                                onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                                className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                                Rent Amount (KES)
                            </label>
                            <input
                                type="number"
                                value={formData.rent_amount}
                                onChange={(e) => setFormData({ ...formData, rent_amount: Number(e.target.value) })}
                                className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                                Status
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value as UnitStatus })}
                                className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20"
                            >
                                <option value="Available">Available</option>
                                <option value="Occupied">Occupied</option>
                                <option value="Maintenance">Maintenance</option>
                                <option value="pending_approval">Pending Approval</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                                Unit Type
                            </label>
                            <select
                                value={formData.unit_type}
                                onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                                className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20"
                            >
                                <option value="Residential">Residential</option>
                                <option value="Commercial">Commercial</option>
                                <option value="Retail">Retail</option>
                                <option value="Office">Office</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                                Bedrooms
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={formData.bedrooms}
                                onChange={(e) => setFormData({ ...formData, bedrooms: Number(e.target.value) })}
                                className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                                Bathrooms
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={formData.bathrooms}
                                onChange={(e) => setFormData({ ...formData, bathrooms: Number(e.target.value) })}
                                className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20"
                            placeholder="Optional description of the unit..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-light-subtle dark:text-dark-subtle mb-3">
                            Amenities
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {allAmenities.map(amenity => (
                                <button
                                    key={amenity}
                                    type="button"
                                    onClick={() => handleToggleAmenity(amenity)}
                                    className={`px-4 py-2 rounded-xl font-medium transition-all border-2 ${
                                        formData.amenities.includes(amenity)
                                            ? "bg-primary/10 border-primary text-primary"
                                            : "bg-light-input dark:bg-dark-input border-transparent text-light-subtle dark:text-dark-subtle hover:border-primary/30"
                                    }`}
                                >
                                    {amenity}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-light-border dark:border-dark-border">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSaving ? (
                                <><RefreshCw size={18} className="animate-spin" /> Saving...</>
                            ) : (
                                <><Edit size={18} /> Save Changes</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

interface DeleteConfirmationModalProps {
    unit: Unit;
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ unit, isOpen, onClose, onConfirm }) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await onConfirm();
            onClose();
        } catch (error) {
            console.error('Failed to delete unit:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-2xl w-full max-w-md shadow-2xl">
                <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                        <AlertTriangle size={24} className="text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-light-text dark:text-dark-text">Delete Unit</h3>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle">This action cannot be undone</p>
                    </div>
                </div>

                <p className="text-light-text dark:text-dark-text mb-6">
                    Are you sure you want to delete <strong>Unit {unit.unit_number}</strong>? 
                    {unit.status === 'Occupied' && (
                        <span className="text-red-600 dark:text-red-400 block mt-2">
                            Warning: This unit is currently occupied. Deleting it may affect tenant records.
                        </span>
                    )}
                </p>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                    >
                        {isDeleting ? (
                            <><RefreshCw size={18} className="animate-spin" /> Deleting...</>
                        ) : (
                            <><Trash2 size={18} /> Delete Unit</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ManageUnits: React.FC = () => {
    const { propertyId, propertyName } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { units, loading, fetchUnits, currentProperty, fetchPropertyDetails, properties, fetchProperties } = useLandlordStore();
    const { success, error: showError, warning } = useToast();

    const [searchTerm, setSearchTerm] = useState('');
    const [unitFilter, setUnitFilter] = useState<UnitStatus | "All">("All");
    const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | "All">("All");
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
    const [deletingUnit, setDeletingUnit] = useState<Unit | null>(null);

    // Check if we're in "all units" mode (no propertyId)
    const isAllUnitsView = !propertyId;
    const displayPropertyName = propertyName || currentProperty?.name || 'All Units';

    useEffect(() => {
        const loadData = async () => {
            if (propertyId) {
                await fetchPropertyDetails(propertyId);
                await fetchUnits(propertyId);
            } else {
                // Load all units across all properties
                await fetchProperties();
                // Fetch units for each property
                const allProps = properties.length > 0 ? properties : (await fetchProperties(), useLandlordStore.getState().properties);
                if (allProps.length > 0) {
                    const allUnitsPromises = allProps.map(p => fetchUnits(p.id));
                    await Promise.all(allUnitsPromises);
                }
            }
        };
        loadData();
    }, [propertyId]);

    const onRefresh = async () => {
        setIsRefreshing(true);
        try {
            if (propertyId) {
                await fetchUnits(propertyId);
            } else {
                const allProps = useLandlordStore.getState().properties;
                const allUnitsPromises = allProps.map(p => fetchUnits(p.id));
                await Promise.all(allUnitsPromises);
            }
            success('Refreshed', 'Units list has been refreshed');
        } catch (error) {
            showError('Error', 'Failed to refresh units');
        } finally {
            setIsRefreshing(false);
        }
    };

    const getStatusIndicator = (status: UnitStatus) => {
        switch (status) {
            case "Occupied":
                return { color: '#0EA5E9', icon: User, label: 'Occupied' }; // Sky Blue
            case "Available":
                return { color: '#10B981', icon: Home, label: 'Available' }; // Emerald
            case "Maintenance":
                return { color: '#64748B', icon: Building2, label: 'Maintenance' }; // Cool Grey
            case "pending_approval":
                return { color: '#4F46E5', icon: Clock, label: 'Pending' }; // Electric Indigo
            default:
                return { color: '#64748B', icon: Home, label: status };
        }
    };

    const filteredUnits = useMemo(() => {
        return units.filter((unit) => {
            const matchesSearch = unit.unit_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (unit.description || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesUnitFilter = unitFilter === "All" || unit.status === unitFilter;
            
            // Payment filter only applies to occupied units
            let matchesPaymentFilter = true;
            if (unit.status === "Occupied" && paymentFilter !== "All") {
                // For now, we'll simulate payment status
                // In a real implementation, this would check actual payment data
                matchesPaymentFilter = paymentFilter === "Paid"; // Simplified for demo
            }

            // Property filter for all-units view
            const matchesPropertyFilter = !propertyId || unit.property_id === propertyId;

            return matchesSearch && matchesUnitFilter && matchesPaymentFilter && matchesPropertyFilter;
        });
    }, [units, searchTerm, unitFilter, paymentFilter, propertyId]);

    const handleUnitPress = (unit: Unit) => {
        navigate(`/landlord/units/detail/${unit.property_id}/${unit.id}`);
    };

    const handleEditUnit = async (updatedData: Partial<Unit>) => {
        if (!editingUnit) return;
        try {
            await apiClient.patch(`/units/${editingUnit.id}`, updatedData);
            success('Unit Updated', `Unit ${editingUnit.unit_number} has been updated successfully`);
            // Refresh units list
            if (propertyId) {
                await fetchUnits(propertyId);
            } else {
                await fetchUnits(editingUnit.property_id);
            }
            setEditingUnit(null);
        } catch (error: any) {
            showError('Update Failed', error.response?.data?.error || 'Failed to update unit');
            throw error;
        }
    };

    const handleDeleteUnit = async () => {
        if (!deletingUnit) return;
        try {
            await apiClient.delete(`/units/${deletingUnit.id}`);
            success('Unit Deleted', `Unit ${deletingUnit.unit_number} has been deleted`);
            // Refresh units list
            if (propertyId) {
                await fetchUnits(propertyId);
            } else {
                await fetchUnits(deletingUnit.property_id);
            }
            setDeletingUnit(null);
        } catch (error: any) {
            showError('Delete Failed', error.response?.data?.error || 'Failed to delete unit');
            throw error;
        }
    };

    const renderFilterButton = (
        title: string,
        value: any,
        currentState: any,
        setState: any
    ) => (
        <button
            onClick={() => setState(value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                currentState === value
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-gray-800'
            }`}
        >
            {title}
        </button>
    );

    const renderUnitCard = (unit: Unit) => {
        const statusStyle = getStatusIndicator(unit.status);
        const StatusIcon = statusStyle.icon;

        return (
            <div
                key={unit.id}
                onClick={() => handleUnitPress(unit)}
                className={`bg-light-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer hover:shadow-lg transition-all duration-200 group relative ${
                    viewMode === 'grid' ? 'p-5' : 'p-4 flex items-center gap-4'
                }`}
            >
                {/* Status Color Bar */}
                <div
                    className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${
                        viewMode === 'list' ? 'rounded-l-xl' : ''
                    }`}
                    style={{ backgroundColor: statusStyle.color }}
                />

                <div className={`flex-1 ${viewMode === 'list' ? 'flex items-center gap-4' : ''}`}>
                    <div className={viewMode === 'list' ? 'flex-1' : ''}>
                        <div className={`mb-3 ${viewMode === 'list' ? 'flex items-center gap-4 mb-0' : ''}`}>
                            <div>
                                <h3 className="text-lg font-bold text-light-text dark:text-dark-text">
                                    Unit {unit.unit_number}
                                </h3>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                    {unit.unit_type} • {unit.bedrooms || 0} Bed, {unit.bathrooms || 0} Bath
                                </p>
                                {isAllUnitsView && currentProperty && (
                                    <p className="text-xs text-primary mt-1">
                                        {currentProperty.name}
                                    </p>
                                )}
                            </div>
                            <div className={viewMode === 'list' ? 'text-right' : 'text-right'}>
                                <p className="text-lg font-bold text-primary">
                                    KES {unit.rent_amount.toLocaleString()}
                                </p>
                                <p className="text-xs text-light-subtle dark:text-dark-subtle">per month</p>
                            </div>
                        </div>

                        {/* Status and Payment Chips */}
                        <div className={`flex flex-wrap items-center gap-2 ${viewMode === 'list' ? 'mb-0' : 'mb-3'}`}>
                            <div
                                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                                style={{
                                    backgroundColor: `${statusStyle.color}20`,
                                    color: statusStyle.color
                                }}
                            >
                                <StatusIcon size={12} />
                                <span>
                                    {unit.status === "pending_approval" ? "Pending" : statusStyle.label}
                                </span>
                            </div>

                            {unit.has_pending_invitation && (
                                <div className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-warning/20 text-warning">
                                    <Mail size={12} />
                                    <span>Lease Pending</span>
                                </div>
                            )}

                            {unit.status === "Occupied" && paymentFilter !== "All" && (
                                <div
                                    className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                                    style={{
                                        backgroundColor: paymentFilter === "Paid" ? '#10B98120' : '#EF444420',
                                        color: paymentFilter === "Paid" ? '#10B981' : '#EF4444'
                                    }}
                                >
                                    <DollarSign size={12} />
                                    <span>{paymentFilter}</span>
                                </div>
                            )}
                        </div>

                        {/* Current Tenant */}
                        {unit.current_tenant && viewMode === 'grid' && (
                            <div className="flex items-center gap-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg mt-3">
                                <img
                                    src={unit.current_tenant.avatar_url || "https://i.pravatar.cc/150?u=default"}
                                    alt={unit.current_tenant.full_name}
                                    className="w-10 h-10 rounded-full"
                                />
                                <div className="flex-1">
                                    <p className="font-medium text-light-text dark:text-dark-text text-sm">
                                        {unit.current_tenant.full_name}
                                    </p>
                                </div>
                                <ChevronRight size={16} className="text-light-subtle dark:text-dark-subtle" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons (visible on hover) */}
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditingUnit(unit);
                        }}
                        className="p-2 bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors"
                        title="Edit Unit"
                    >
                        <Edit size={16} className="text-primary" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setDeletingUnit(unit);
                        }}
                        className="p-2 bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete Unit"
                    >
                        <Trash2 size={16} className="text-red-600" />
                    </button>
                </div>
            </div>
        );
    };

    if (loading && units.length === 0) {
        return <LoadingScreen />;
    }

    return (
        <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-20">
            {/* Header */}
            <div className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 md:px-6 py-4 sticky top-0 z-10">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(isAllUnitsView ? '/landlord/dashboard' : `/landlord/properties/${propertyId}`)}
                            className="p-2 hover:bg-light-bg dark:hover:bg-gray-800 rounded-full transition-colors"
                        >
                            <ArrowLeft size={20} className="text-light-subtle dark:text-dark-subtle" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
                                {displayPropertyName}
                            </h1>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                {filteredUnits.length} unit{filteredUnits.length !== 1 ? 's' : ''} found
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onRefresh}
                        disabled={isRefreshing}
                        className="p-2 hover:bg-light-bg dark:hover:bg-gray-800 rounded-full transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw size={20} className={`text-light-subtle dark:text-dark-subtle ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="relative mb-4">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
                    <input
                        type="text"
                        placeholder="Search units by number or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text placeholder-light-placeholder dark:placeholder-dark-placeholder focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                </div>

                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Unit Status Filters */}
                    <div className="flex flex-wrap gap-2">
                        {renderFilterButton("All", "All", unitFilter, setUnitFilter)}
                        {renderFilterButton("Occupied", "Occupied", unitFilter, setUnitFilter)}
                        {renderFilterButton("Available", "Available", unitFilter, setUnitFilter)}
                        {renderFilterButton("Maintenance", "Maintenance", unitFilter, setUnitFilter)}
                    </div>

                    {/* View Mode Toggle */}
                    <div className="ml-auto flex items-center gap-2 bg-light-input dark:bg-dark-input rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'grid'
                                    ? 'bg-primary text-white'
                                    : 'text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-gray-800'
                            }`}
                            title="Grid View"
                        >
                            <Grid size={18} />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-lg transition-colors ${
                                viewMode === 'list'
                                    ? 'bg-primary text-white'
                                    : 'text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-gray-800'
                            }`}
                            title="List View"
                        >
                            <List size={18} />
                        </button>
                    </div>
                </div>

                {/* Payment Status Filters (only show when Occupied is selected) */}
                {unitFilter === "Occupied" && (
                    <div className="mt-4 pt-4 border-t border-light-border dark:border-dark-border">
                        <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle mb-2">
                            Filter by payment status:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {renderFilterButton("All", "All", paymentFilter, setPaymentFilter)}
                            {renderFilterButton("Paid", "Paid", paymentFilter, setPaymentFilter)}
                            {renderFilterButton("Due", "Due", paymentFilter, setPaymentFilter)}
                            {renderFilterButton("Overdue", "Overdue", paymentFilter, setPaymentFilter)}
                        </div>
                    </div>
                )}
            </div>

            {/* Units Grid/List */}
            <div className="p-4 md:p-6">
                {filteredUnits.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-light-card dark:bg-dark-card mb-4">
                            <Home size={40} className="text-light-subtle dark:text-dark-subtle" />
                        </div>
                        <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-2">
                            No units found
                        </h3>
                        <p className="text-light-subtle dark:text-dark-subtle mb-6 max-w-md mx-auto">
                            {searchTerm || unitFilter !== "All" 
                                ? "No units match your current filters. Try adjusting your search or filters."
                                : "This property doesn't have any units yet. Get started by adding your first unit."
                            }
                        </p>
                        {!searchTerm && unitFilter === "All" && (
                            <button
                                onClick={() => navigate(`/landlord/units/register?propertyId=${propertyId}&propertyName=${encodeURIComponent(displayPropertyName)}`)}
                                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors"
                            >
                                <Plus size={20} />
                                Add Your First Unit
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={viewMode === 'grid' 
                        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                        : 'flex flex-col gap-3'
                    }>
                        {filteredUnits.map((unit) => renderUnitCard(unit))}
                    </div>
                )}
            </div>

            {/* Floating Add Button */}
            <button
                onClick={() => navigate(`/landlord/units/register?propertyId=${propertyId}&propertyName=${encodeURIComponent(displayPropertyName)}`)}
                className="fixed bottom-6 right-6 bg-primary hover:bg-primary/90 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-105 active:scale-95 z-20"
                title="Add New Unit"
            >
                <Plus size={24} />
            </button>

            {/* Edit Unit Modal */}
            {editingUnit && (
                <EditUnitModal
                    unit={editingUnit}
                    isOpen={!!editingUnit}
                    onClose={() => setEditingUnit(null)}
                    onSave={handleEditUnit}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deletingUnit && (
                <DeleteConfirmationModal
                    unit={deletingUnit}
                    isOpen={!!deletingUnit}
                    onClose={() => setDeletingUnit(null)}
                    onConfirm={handleDeleteUnit}
                />
            )}
        </div>
    );
};

export default ManageUnits;